// src/components/financials/ContributionStatementGenerator.js
// Updated: 01/25/2026 - Aligned Bulk Layout with Letterhead Design

import React, { useState } from 'react';
import { 
  Box, Typography, Paper, Grid, TextField, Button, 
  CircularProgress, Alert, Divider, Table, TableBody, 
  TableCell, TableHead, TableRow, TableContainer, LinearProgress 
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ContributionStatementGenerator = () => {
  const { userProfile } = useAuth();
  const churchId = userProfile?.churchId;
  
  const [year, setYear] = useState(new Date().getFullYear() - 1); 
  const [statements, setStatements] = useState([]);
  const [churchInfo, setChurchInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);

  const fetchMemberAddress = async (userId) => {
    try {
        const detailRef = doc(db, 'users', userId, 'privateContactInfo', 'details');
        const detailSnap = await getDoc(detailRef);
        if (detailSnap.exists()) {
            const d = detailSnap.data();
            if (d.address || d.addrStreet || d.city || d.addrCity) {
                return {
                    address: d.address || d.addrStreet || '',
                    city: d.city || d.addrCity || '',
                    state: d.state || d.addrState || '',
                    zip: d.zip || d.addrZip || ''
                };
            }
        }
    } catch (e) { console.warn(e); }
    return null;
  };

  const generateStatements = async () => {
    if (!churchId) return;
    setLoading(true);
    setHasRun(false);

    try {
      const churchDocRef = doc(db, 'churches', churchId);
      const churchSnap = await getDoc(churchDocRef);
      let churchMeta = { name: "Church Name", address: "", city: "", state: "", zip: "", taxId: "", logoUrl: "" };
      if (churchSnap.exists()) {
          const data = churchSnap.data();
          const lp = data.legalProfile || {}; 
          churchMeta = {
              name: lp.legalName || data.name || "Church Name",
              logoUrl: lp.logoUrl || data.logoUrl || "", 
              address: lp.address || data.address || "",
              city: lp.city || data.city || "",
              state: lp.state || data.state || "",
              zip: lp.zip || data.zip || "",
              taxId: lp.taxId || ""
          };
      }
      setChurchInfo(churchMeta);

      const unitsSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'givingUnits'));
      const memberToUnitMap = {}; 
      const unitDetails = {}; 
      unitsSnap.docs.forEach(doc => {
        const d = doc.data();
        unitDetails[doc.id] = { 
            id: doc.id, 
            name: d.name, 
            addressObj: { address: d.address || '', city: d.city || '', state: d.state || '', zip: d.zip || '' },
            memberIds: d.memberIds || []
        };
        if (d.memberIds) d.memberIds.forEach(mId => memberToUnitMap[mId] = doc.id);
      });

      const q = query(
        collection(db, 'churches', churchId, 'financials', 'data', 'transactions'),
        where('status', '==', 'posted'),
        where('date', '>=', `${year}-01-01`),
        where('date', '<=', `${year}-12-31`)
      );
      const snap = await getDocs(q);

      const groupedData = {};
      snap.docs.forEach(doc => {
        const t = doc.data();
        if (t.type === 'opening_balance') return;
        const rawId = t.donorId || t.donorName || "Unknown";
        const unitId = memberToUnitMap[rawId] || rawId; 
        if (!groupedData[unitId]) {
            const staticInfo = unitDetails[unitId];
            groupedData[unitId] = { id: unitId, name: staticInfo?.name || t.donorName || "Guest Donor", rawAddress: staticInfo?.addressObj, memberIds: staticInfo?.memberIds || [t.donorId], gifts: [], total: 0 };
        }
        const amt = parseFloat(t.amount) || 0;
        if (t.splits && t.splits.length > 0) {
            t.splits.forEach(s => {
                 const splitAmt = parseFloat(s.amount) || 0;
                 groupedData[unitId].gifts.push({ date: t.date, fund: s.fundName || "General", amount: splitAmt, method: t.method });
                 groupedData[unitId].total += splitAmt;
            });
        } else {
            groupedData[unitId].gifts.push({ date: t.date, fund: "General", amount: amt, method: t.method });
            groupedData[unitId].total += amt;
        }
      });

      const resolvedStatements = await Promise.all(
          Object.values(groupedData).map(async (group) => {
              let addr = group.rawAddress;
              if ((!addr || !addr.address) && group.memberIds?.[0]) {
                  const memberAddr = await fetchMemberAddress(group.memberIds[0]);
                  if (memberAddr) addr = memberAddr;
              }
              let fullAddress = "";
              if (addr && (addr.address || addr.city)) {
                  fullAddress = `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}`;
              }
              return { ...group, formattedAddress: fullAddress };
          })
      );
      setStatements(resolvedStatements.sort((a,b) => a.name.localeCompare(b.name)));
      setHasRun(true);
    } catch (e) { alert("Error: " + e.message); }
    setLoading(false);
  };

  const downloadAllPDF = async () => {
    const elements = document.querySelectorAll('.statement-page');
    if (elements.length === 0) return;
    setDownloading(true);
    setProgress(0);
    try {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            const canvas = await html2canvas(element, { 
                scale: 2, 
                useCORS: true, 
                allowTaint: true, // Ensured consistent options
                logging: false 
            });
            const imgData = canvas.toDataURL('image/png');
            const imgHeight = (canvas.height * pdfWidth) / canvas.width;
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight);
            if (i < elements.length - 1) pdf.addPage();
            setProgress(Math.round(((i + 1) / elements.length) * 100));
        }
        pdf.save(`All_Contribution_Statements_${year}.pdf`);
    } catch (e) { alert("PDF Error: " + e.message); }
    setDownloading(false);
    setProgress(0);
  };

  return (
    <Box>
      <Paper className="no-print" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6">Bulk Annual Contribution Statements</Typography>
        <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item><TextField type="number" label="Year" size="small" value={year} onChange={(e) => setYear(e.target.value)} /></Grid>
            <Grid item><Button variant="contained" onClick={generateStatements} disabled={loading}>{loading ? <CircularProgress size={24} /> : "Generate All"}</Button></Grid>
            {hasRun && <Grid item><Button variant="outlined" startIcon={downloading ? <CircularProgress size={20} /> : <Download />} onClick={downloadAllPDF} disabled={downloading}>{downloading ? `${progress}%` : "Download Batch PDF"}</Button></Grid>}
        </Grid>
        {downloading && <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} />}
      </Paper>

      <Box id="bulk-statement-print-area">
          {statements.map((stmt) => (
              <Paper 
                key={stmt.id} 
                elevation={0}
                className="statement-page" 
                sx={{ 
                    p: '20mm', mb: 5, border: '1px solid #ddd', bgcolor: 'white',
                    width: '210mm', minHeight: '297mm', mx: 'auto', boxSizing: 'border-box'
                }}
              >
                  {/* --- HEADER GRID (Matches Individual) --- */}
                  <Grid container alignItems="center" spacing={4} sx={{ mb: 2, borderBottom: '1px solid #ccc', pb: 2 }}>
                      
                      {/* LEFT: LOGO */}
                      <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                          {churchInfo?.logoUrl && (
                              <img 
                                src={churchInfo.logoUrl} 
                                alt="Logo" 
                                crossOrigin="anonymous" 
                                style={{ maxHeight: '90px', maxWidth: '100%', objectFit: 'contain' }} 
                              />
                          )}
                      </Grid>

                      {/* CENTER: ADDRESS */}
                      <Grid item xs={6} sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'uppercase', lineHeight: 1.2 }}>
                              {churchInfo?.name}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ lineHeight: 1.1 }}>
                              {churchInfo?.address}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ lineHeight: 1.1 }}>
                              {churchInfo?.city}{churchInfo?.city && ","} {churchInfo?.state} {churchInfo?.zip}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                              EIN: {churchInfo?.taxId || "Pending"}
                          </Typography>
                      </Grid>

                      {/* RIGHT: SPACER */}
                      <Grid item xs={3} />
                  </Grid>

                  {/* --- TITLE & YEAR --- */}
                  <Typography variant="h5" align="center" sx={{ mt: 6, mb: 6, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 'bold' }}>
                      Contribution Statement {year}
                  </Typography>

                  {/* --- DONOR ADDRESS BOX --- */}
                  <Box sx={{ mb: 4, p: 2, border: '1px solid #eee', borderRadius: 1, width: '60%' }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>{stmt.name}</Typography>
                      {stmt.formattedAddress ? (
                          <Typography variant="body1">{stmt.formattedAddress}</Typography>
                      ) : (
                          <Typography variant="caption" color="error" fontStyle="italic">(No Address on File)</Typography>
                      )}
                  </Box>

                  <TableContainer component={Box} sx={{ border: '1px solid #000' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#eeeeee', borderBottom: '1px solid #000' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Fund</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Method</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stmt.gifts.map((g, i) => (
                                <TableRow key={i} sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}>
                                    <TableCell>{g.date}</TableCell>
                                    <TableCell>{g.fund}</TableCell>
                                    <TableCell>{g.method}</TableCell>
                                    <TableCell align="right">${g.amount.toFixed(2)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 2, textAlign: 'right', pr: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                          Total: ${stmt.total.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </Typography>
                  </Box>

                  <Box sx={{ mt: 8, textAlign: 'center', px: 4 }}>
                      <Typography variant="caption" fontStyle="italic" display="block">"No goods or services were provided in exchange for these contributions, other than intangible religious benefits."</Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Thank you for your support.</Typography>
                  </Box>
              </Paper>
          ))}
      </Box>
    </Box>
  );
};

export default ContributionStatementGenerator;